import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, useToastActions, ConfirmationModal, DescriptionList, SettingsSaveBar, useDirtyForm } from '@airclou/ui';
import { Trash2 } from 'lucide-react';
import { updateCompany, deleteCompany, type Company, type UpdateCompanyInput } from '@/api/companies';
import { useNavigate } from 'react-router-dom';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateTimeInTz } from '@airclou/utils'
import { useAuth } from '../../contexts/AuthContext'
import { canManageOrg } from '../../navigation'

interface CompanySettingsTabProps {
  company: Company;
  onUpdate: () => void;
}

export function CompanySettingsTab({ company, onUpdate }: CompanySettingsTabProps) {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const { user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const { success, error } = useToastActions();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const form = useDirtyForm<UpdateCompanyInput>({
    name: company.name,
    slug: company.slug,
  });
  const formData = form.values;
  const hasChanges = form.isDirty;

  // Deleting a company is the owner's, and only the owner's — the API gates it
  // on TENANT_ROLE_SETS.OWNER_ONLY because it soft-deletes every workspace under
  // the company too. Read `orgRole`, not `role`: `role` is the dashboard
  // app-access role, a different vocabulary that never says 'owner', so gating
  // on it would hide the button from the actual owner. `orgRole` is
  // `tenant_users.role` for the session tenant, which is exactly what
  // `requireTenantRole` resolves — so what this disables is what the API
  // refuses. (Editing name and slug stays MANAGE, so admins keep the form.)
  const isOwner = user?.orgRole === 'owner';

  // Editing the name and slug is `PATCH /api/companies/:id`, which is MANAGE —
  // a wider set than Delete. Without this the fields stayed live for a viewer:
  // they would type, `SettingsSaveBar` would appear, Save would 403, and because
  // the save failed the form stays dirty and the bar stays up with Discard as
  // the only way out. Disabled rather than hidden, so the values are still
  // readable — a settings page that shows nothing is worse than one you cannot
  // edit.
  const canManage = canManageOrg(user?.orgRole ?? undefined);

  // Three states, not two: `orgRole` is undefined while /context is in flight
  // AND when it failed, and "we do not know your role" is not "you are not the
  // owner". The button stays disabled either way — that is the safe default —
  // only the explanation changes.
  const roleKnown = !isAuthLoading && user?.orgRole !== undefined;

  // The company object this form has already taken on. The parent hands down a
  // fresh object on every load, so object identity is what says "the server
  // sent something new" — the values cannot say it, because the effect also
  // re-runs on the dirty->clean transition a save produces, at which point
  // `company` is still the pre-save prop and adopting it would replace the
  // rename the operator just committed with the old name.
  const adopted = useRef<Company | null>(null);

  // `updated_at` of the row this form last persisted. It is the server's own
  // ordering signal, and the only reliable way to recognise a prop carrying
  // data from before that save — a load that was already in flight, or one
  // that arrived while the request was. Adopting one of those would put the
  // old name back over what was just stored, on a form reporting itself clean.
  const savedAt = useRef<string | null>(null);

  // Adopt server state only when it is genuinely new, is not older than our own
  // last save, and the form has nothing unsaved in it. Each guard is here
  // because the parent fires `loadData` without awaiting it after a save, so a
  // load routinely lands while the operator is typing again. isDirty is a
  // dependency so state skipped while dirty is adopted the moment the form goes
  // clean (a Discard, or an edit typed back to its stored value).
  useEffect(() => {
    if (adopted.current === company) return;
    // A different record in the same mounted form. Nothing we remember applies
    // to it — including `savedAt`, which orders row versions within one row —
    // so take it wholesale and forget the previous company. The parent keys the
    // tabs by company so this should not happen; the cost of being wrong is an
    // edit saved onto the wrong company, so the form does not rely on that.
    if (adopted.current && adopted.current.id !== company.id) {
      adopted.current = company;
      savedAt.current = null;
      form.setBaseline({ name: company.name, slug: company.slug });
      return;
    }
    // Older than our own last save: this form holds the newer state.
    if (savedAt.current && Date.parse(company.updated_at) <= Date.parse(savedAt.current)) {
      adopted.current = company;
      return;
    }
    if (form.isDirty) return;

    const incoming = { name: company.name, slug: company.slug };
    const clean = form.baseline;
    adopted.current = company;
    // Move the baseline, then take the values only if nothing has been typed
    // since this render. `setBaseline` replaces the values outright, so a
    // keystroke enqueued between the render and this write would be discarded;
    // reading `prev` is what sees it. Keeping the edit against the new baseline
    // is the honest outcome — it reads as unsaved, which it is.
    form.markSaved(incoming);
    form.setValues((prev) =>
      prev.name === clean.name && prev.slug === clean.slug ? incoming : prev
    );
  }, [company, form.isDirty]);

  const handleChange = (field: keyof UpdateCompanyInput, value: string) => {
    form.setField(field, value);
  };

  const validationMessage = useMemo<string | null>(
    () => (formData.name?.trim() && formData.slug?.trim() ? null : t('companies.nameSlugRequired')),
    [formData.name, formData.slug, t]
  );

  const handleSave = async () => {
    if (validationMessage) {
      error(validationMessage, t('companies.validationError'));
      return;
    }

    setIsSaving(true);
    try {
      const saved = await updateCompany(company.id, formData);
      success(t('companies.companyUpdated'), t('common.success'));
      // markSaved, not setBaseline: the values already hold what we sent, so
      // only the baseline needs to move.
      form.markSaved(formData);
      // Everything the server held before this row version is now superseded.
      savedAt.current = saved.company.updated_at;
      onUpdate();
    } catch (err: any) {
      error(err.message || t('companies.failedToUpdate'), t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteCompany(company.id);
      success(t('companies.companyDeleted'), t('common.success'));
      navigate('/companies');
    } catch (err: any) {
      error(err.message || t('companies.failedToDelete'), t('common.error'));
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  const handleReset = () => {
    form.reset();
  };

  return (
    <div className="max-w-2xl space-y-6">
      {/* General Settings */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{t('companies.generalSettings')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('companies.basicInfo')}
          </p>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <label htmlFor="company-name" className="block text-sm font-medium text-foreground mb-2">
              {t('companies.companyName')}
            </label>
            <Input
              id="company-name"
              type="text"
              value={formData.name || ''}
              onChange={(value) => handleChange('name', value)}
              placeholder="e.g., Engineering"
              disabled={isSaving || !canManage}
            />
          </div>

          <div>
            <label htmlFor="company-slug" className="block text-sm font-medium text-foreground mb-2">
              {t('common.slug')}
            </label>
            <Input
              id="company-slug"
              type="text"
              value={formData.slug || ''}
              onChange={(value) => handleChange('slug', value)}
              placeholder="e.g., engineering"
              disabled={isSaving || !canManage}
            />
            <p className="mt-2 text-xs text-muted-foreground">
              {t('companies.slugUrlHint')}
            </p>
          </div>

          {roleKnown && !canManage && (
            <p className="text-sm text-muted-foreground">
              {t('companies.onlyAdminsCanEdit')}
            </p>
          )}
          {!roleKnown && !isAuthLoading && (
            <p className="text-sm text-muted-foreground">
              {t('companies.roleUnknownReload')}
            </p>
          )}
        </div>
      </div>

      {/* Company Information */}
      <DescriptionList
        title={t('companies.companyInfo')}
        description={t('companies.additionalDetails')}
        items={[
          {
            term: t('companies.companyId'),
            description: (
              <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                {company.id}
              </code>
            ),
          },
          {
            term: t('common.created'),
            description: formatDateTimeInTz(company.created_at, timezone),
          },
          {
            term: t('companies.lastUpdated'),
            description: formatDateTimeInTz(company.updated_at, timezone),
          },
          {
            term: t('companies.tenantId'),
            description: (
              <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                {company.tenant_id}
              </code>
            ),
          },
        ]}
      />

      {/* Danger Zone */}
      <div className="bg-card rounded-lg border border-red-200 dark:border-red-800">
        <div className="p-6 border-b border-red-200 dark:border-red-800">
          <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">{t('companies.dangerZone')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('companies.irreversibleActions')}
          </p>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-foreground">{t('companies.deleteThisCompany')}</h4>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('companies.deleteWarning')}
              </p>
            </div>
            <Button
              variant="outline"
              disabled={isSaving || !isOwner}
              onClick={() => setIsDeleteModalOpen(true)}
              className="text-red-600 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('companies.deleteCompany')}
            </Button>
          </div>
          {/* Only the "you are not the owner" half here. The "we could not
              confirm your role" half is printed once, above the fields, because
              it is a fact about the page rather than about this button — and
              saying it twice on one screen reads as two separate problems. */}
          {roleKnown && !isOwner && (
            <p className="mt-4 text-sm text-muted-foreground">
              {t('companies.onlyOwnerCanDelete')}
            </p>
          )}
        </div>
      </div>

      <SettingsSaveBar
        visible={hasChanges}
        onSave={handleSave}
        onDiscard={handleReset}
        isSaving={isSaving}
        saveDisabled={isDeleting || Boolean(validationMessage)}
        message={validationMessage ?? t('common.unsavedChanges')}
        saveLabel={t('common.save')}
        savingLabel={t('common.saving')}
        discardLabel={t('common.discard')}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={t('companies.deleteCompany')}
        description={t('companies.deleteConfirmFull', { name: company.name })}
        variant="danger"
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
